import {defineStore} from 'pinia'
import {useApiRequestStore} from '@toolify/client/src/stores/ApiRequestStore/useApiRequestStore'
import {
  IGetLatestDeploymentVersionRequestInterfaces,
} from '@toolify/server/src/controllers/DeploymentVersionController/validators/GetLatestDeploymentVersion/types/IGetLatestDeploymentVersionRequestInterfaces'
import {
  IListDeploymentVersionsRequestInterfaces,
} from '@toolify/server/src/controllers/DeploymentVersionController/validators/ListDeploymentVersions/types/IListDeploymentVersionsRequestInterfaces'
import {
  IListDeploymentVersionItemBlocksRequestInterfaces,
} from '@toolify/server/src/controllers/DeploymentVersionController/validators/ListDeploymentVersionItemBlocks/types/IListDeploymentVersionItemBlocksRequestInterfaces'
import {
  IMarkDeploymentVersionItemSeenRequestInterfaces,
} from '@toolify/server/src/controllers/DeploymentVersionController/validators/MarkDeploymentVersionItemSeen/types/IMarkDeploymentVersionItemSeenRequestInterfaces'
import {mapDeploymentVersionResponseInstance} from '@toolify/client/src/modules/api/singletons/mapDeploymentVersionResponseInstance'
import {mapDeploymentVersionItemBlockResponseInstance} from '@toolify/client/src/modules/api/singletons/mapDeploymentVersionItemBlockResponseInstance'
import {IDeploymentVersionResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDeploymentVersionResponseInstance'
import {IDeploymentVersionItemBlockResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDeploymentVersionItemBlockResponseInstance'
import {AppPlatformType} from '@toolify/server/src/models/mongoose/SessionModel/enum/AppPlatformType'

export const useDeploymentVersionApiStore = defineStore('deploymentVersionApi', () => {
  const apiRequestStore = useApiRequestStore()

  async function getLatestDeploymentVersion(
    appPlatformType: AppPlatformType,
    versionId?: string,
  ): Promise<{
    deploymentVersion: IDeploymentVersionResponseInstance | null
    runningDeploymentVersion: IDeploymentVersionResponseInstance | null
  }> {
    const {deploymentVersion, runningDeploymentVersion} = await apiRequestStore.get<IGetLatestDeploymentVersionRequestInterfaces>(
      `/deployment-versions/app-platform/${appPlatformType}/latest`,
      {
        params: {
          versionId,
        },
      },
    )
    return {
      deploymentVersion: deploymentVersion
        ? mapDeploymentVersionResponseInstance(deploymentVersion)
        : null,
      runningDeploymentVersion: runningDeploymentVersion
        ? mapDeploymentVersionResponseInstance(runningDeploymentVersion)
        : null,
    }
  }

  async function listDeploymentVersions(
    appPlatformType: AppPlatformType,
  ): Promise<IDeploymentVersionResponseInstance[]> {
    const {deploymentVersions} = await apiRequestStore.get<IListDeploymentVersionsRequestInterfaces>(
      `/deployment-versions/${appPlatformType}`,
    )
    return deploymentVersions.map(deploymentVersion => mapDeploymentVersionResponseInstance(deploymentVersion))
  }

  async function listBlocks(deploymentVersionId: string): Promise<IDeploymentVersionItemBlockResponseInstance[]> {
    const {deploymentVersionItemBlocks} = await apiRequestStore.get<IListDeploymentVersionItemBlocksRequestInterfaces>(
      `/deployment-versions/${deploymentVersionId}/blocks`,
    )
    return deploymentVersionItemBlocks.map(block => mapDeploymentVersionItemBlockResponseInstance(block))
  }

  async function markItemSeen(deploymentVersionItemId: string): Promise<void> {
    await apiRequestStore.post<IMarkDeploymentVersionItemSeenRequestInterfaces>(
      `/deployment-version-items/${deploymentVersionItemId}/seen`,
    )
  }

  return {
    getLatestDeploymentVersion,
    listDeploymentVersions,
    listBlocks,
    markItemSeen,
  }
})
